import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mic, MicOff, Paperclip, BookOpen, Trash2, FileText, Square } from "lucide-react";
import { toast } from "sonner";

// Light wrapper around the browser SpeechRecognition API. Returns the API or
// null if the browser doesn't support it.
function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function HomeworkDialog({ open, onClose, period, classRoom, section, availableSections = [], date, onSaved }) {
  const [tab, setTab] = useState("file");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // Sections this homework will be assigned to. Defaults to the section the
  // teacher is currently viewing; they can tick additional sections of the
  // same class they teach so the same homework isn't re-entered per section.
  const [sections, setSections] = useState([section]);

  const sectionChoices = [...new Set([section, ...availableSections].filter(Boolean))].sort();
  const toggleSection = (s) =>
    setSections((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  // Speech state
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [sttSupported, setSttSupported] = useState(true);

  useEffect(() => {
    setSttSupported(!!getSpeechRecognition());
  }, []);

  // Reset when dialog opens for a new period
  useEffect(() => {
    if (open) {
      setTitle(period?.subject || "");
      setDescription("");
      setFile(null);
      setTab("file");
      setSections([section]);
    }
    if (!open && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {} // ignore
      recognitionRef.current = null;
      setListening(false);
    }
  }, [open, period]);

  const startListening = () => {
    const Rec = getSpeechRecognition();
    if (!Rec) {
      toast.error("Speech recognition isn't supported in this browser. Try Chrome or Edge.");
      return;
    }
    const r = new Rec();
    r.lang = navigator.language || "en-IN";
    r.interimResults = true;
    r.continuous = true;
    let finalised = description ? description + " " : "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalised += t.trim() + " ";
        else interim += t;
      }
      setDescription((finalised + interim).replace(/\s+/g, " ").trim());
    };
    r.onerror = (e) => {
      console.warn("Speech recognition error:", e.error);
      if (e.error === "not-allowed") toast.error("Microphone permission was denied.");
      else if (e.error !== "aborted") toast.warning(`Speech: ${e.error}`);
      setListening(false);
    };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    r.start();
    setListening(true);
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {} // ignore
    setListening(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!description.trim() && !file) {
      toast.error("Add a description, a file, or both.");
      return;
    }
    const targets = sections.length ? sections : [section];
    setSaving(true);
    try {
      let last = null;
      // A FormData with a File is single-use, so build a fresh payload per
      // section. Sequential keeps ordering + error messages clear.
      for (const sec of targets) {
        const fd = new FormData();
        fd.append("class_name", classRoom);
        fd.append("section", sec);
        fd.append("date", date);
        fd.append("period", String(period?.period ?? 0));
        fd.append("subject", period?.subject || "");
        fd.append("title", title);
        fd.append("description", description);
        if (file) fd.append("file", file);
        const { data } = await api.post("/homework", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        last = data;
      }
      toast.success(
        targets.length > 1
          ? `Homework saved to ${targets.length} sections (${targets.join(", ")})`
          : "Homework saved"
      );
      onSaved?.(last);
      onClose?.();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not save homework");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="homework-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{fontFamily:"Outfit"}}>
            <BookOpen className="h-5 w-5 text-sky-500" /> Assign homework
          </DialogTitle>
          <DialogDescription>
            Class <b>{classRoom}-{section}</b> · Period <b>{period?.period}</b> · Subject <b>{period?.subject}</b> · {date}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="homework-form">
          <div className="space-y-1.5">
            <Label htmlFor="hw-title">Title</Label>
            <Input id="hw-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Worksheet 4 — fractions" data-testid="hw-title-input" className="h-10 rounded-lg"/>
          </div>

          {sectionChoices.length > 1 && (
            <div className="space-y-1.5" data-testid="hw-sections">
              <Label>Assign to sections</Label>
              <div className="flex flex-wrap gap-2">
                {sectionChoices.map((s) => {
                  const active = sections.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSection(s)}
                      data-testid={`hw-section-${s}`}
                      aria-pressed={active}
                      className={
                        "px-3 h-9 rounded-full text-sm font-semibold border transition-colors " +
                        (active
                          ? "bg-sky-500 border-sky-500 text-white"
                          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-sky-300")
                      }
                    >
                      {classRoom}-{s}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400">Same homework will be posted to each selected section.</p>
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab} data-testid="hw-tabs">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="file" data-testid="hw-tab-file"><Paperclip className="h-4 w-4 mr-1"/>Document</TabsTrigger>
              <TabsTrigger value="voice" data-testid="hw-tab-voice"><Mic className="h-4 w-4 mr-1"/>Voice</TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="space-y-3 pt-3">
              <Label htmlFor="hw-file">Attach a document (≤ 5 MB)</Label>
              <Input
                id="hw-file"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,application/pdf,image/*,text/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="hw-file-input"
                className="h-10 rounded-lg"
              />
              {file && (
                <div className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="hw-file-chip">
                  <span className="inline-flex items-center gap-2 truncate"><FileText className="h-4 w-4 text-slate-500"/>{file.name} · {(file.size/1024).toFixed(1)} KB</span>
                  <button type="button" onClick={() => setFile(null)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4"/></button>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="hw-note">Notes (optional)</Label>
                <textarea id="hw-note" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Anything you want the students to know about this attachment…"
                  data-testid="hw-notes-input"
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>
            </TabsContent>

            <TabsContent value="voice" className="space-y-3 pt-3">
              <div className="flex items-center justify-between">
                <Label>Dictate the homework</Label>
                {!sttSupported && (
                  <span className="text-xs text-amber-600">Voice not supported in this browser</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!listening ? (
                  <Button type="button" onClick={startListening} disabled={!sttSupported} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="hw-mic-start">
                    <Mic className="h-4 w-4 mr-1" /> Start recording
                  </Button>
                ) : (
                  <Button type="button" onClick={stopListening} variant="outline" className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="hw-mic-stop">
                    <Square className="h-4 w-4 mr-1" /> Stop
                  </Button>
                )}
                <span className={`inline-flex items-center gap-1 text-xs ${listening ? "text-rose-600" : "text-slate-400"}`}>
                  {listening ? <><MicOff className="h-3 w-3 animate-pulse" /> Listening — speak naturally…</> : "Idle"}
                </span>
              </div>
              <textarea
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="The transcript will appear here. You can also type or edit freely."
                data-testid="hw-voice-transcript"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
              <p className="text-xs text-slate-400">Powered by your browser. Edit anything that didn't transcribe quite right before saving.</p>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="hw-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving || sections.length === 0} className="bg-sky-500 hover:bg-sky-600" data-testid="hw-save-btn">
              {saving ? "Saving…" : (sections.length > 1 ? `Save to ${sections.length} sections` : "Save homework")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
